import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { loginApi, ecpVerifyApi } from "../api";
import { useAuth } from "../context/AuthContext";
import FeedbackModal from "../components/FeedbackModal";
import LanguageSwitcher from "../components/LanguageSwitcher";
import EcpButton from "../components/EcpButton";
import { normalizeApiError } from "../lib/apiError";

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await loginApi({ email: email.trim(), password });
      await refresh();
      navigate("/dashboard");
    } catch (e2) {
      setErr(normalizeApiError(e2, t("common.error")));
    } finally { setBusy(false); }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card" data-testid="login-card">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Link to="/" className="small text-muted text-decoration-none"
                data-testid="login-back-home" title={t("auth.back_to_home")}>
            <i className="bi bi-arrow-left me-1"></i>{t("auth.back_to_home")}
          </Link>
          <LanguageSwitcher />
        </div>
        <div className="text-center mb-4">
          <Link to="/" className="text-decoration-none" data-testid="login-brand-home"
                title={t("auth.back_to_home")}>
            <div className="d-inline-flex align-items-center justify-content-center mb-2"
                 style={{ width: 56, height: 56, background: "#0b3d91", borderRadius: 14, color: "white", cursor: "pointer" }}>
              <i className="bi bi-clock-history fs-3"></i>
            </div>
            <h3 className="fw-bold mb-1 text-dark">Tabel</h3>
          </Link>
          <p className="text-muted small mb-0">{t("auth.login_subtitle")}</p>
        </div>

        {err && <div className="alert alert-danger py-2" data-testid="login-error">{err}</div>}

        <form onSubmit={onSubmit}>
          <div className="mb-3">
            <label className="form-label small">{t("common.email")}</label>
            <input type="email" className="form-control" required name="email" value={email}
                   onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
          </div>
          <div className="mb-2">
            <label className="form-label small">{t("common.password")}</label>
            <input type="password" className="form-control" required name="password" value={password}
                   onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
          </div>
          <div className="d-flex justify-content-end mb-3">
            <Link to="/forgot-password" className="small" data-testid="login-link-forgot">{t("auth.forgot_password")}</Link>
          </div>
          <button className="btn btn-primary w-100" disabled={busy} data-testid="login-submit">
            {busy ? t("common.loading") : t("auth.login_button")}
          </button>
        </form>

        <div className="mt-3">
          <EcpButton
            label="Войти через ЭЦП НУЦ РК"
            testId="login-ecp"
            onSigned={async (challenge_id, cms_b64) => {
              const resp = await ecpVerifyApi(challenge_id, cms_b64);
              if (resp?.suggest_company && !resp.suggest_company.exists) {
                try {
                  sessionStorage.setItem("ecp_suggest_company", JSON.stringify(resp.suggest_company));
                } catch (_e) { /* ignore */ }
              }
              await refresh();
              navigate("/dashboard");
              return resp;
            }}
          />
        </div>

        <div className="text-center mt-4 small">
          {t("auth.no_account")} <Link to="/register" data-testid="login-link-register">{t("auth.register")}</Link>
        </div>
        <div className="text-center mt-2 text-muted" style={{ fontSize: 12 }} data-testid="login-legal-hint">
          {t("legal.login_hint")}{" "}
          <Link to="/terms" target="_blank" className="text-muted">{t("legal.terms_link")}</Link>
          {" "}{t("legal.and")}{" "}
          <Link to="/privacy" target="_blank" className="text-muted">{t("legal.privacy_link")}</Link>
        </div>
        <div className="text-center mt-3 small">
          <button type="button" className="btn btn-link p-0 small text-muted"
                  onClick={() => setShowFeedback(true)}
                  data-testid="login-feedback-link">
            <i className="bi bi-headset me-1"></i>
            {t("auth.contact_admin")}
          </button>
        </div>
      </div>
      <FeedbackModal open={showFeedback} onClose={() => setShowFeedback(false)} />
    </div>
  );
}
